import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const SurveyStats = () => {
    const [stats, setStats] = useState(null);
    const [speakers, setSpeakers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDate, setFilterDate] = useState('');
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedCharts, setSelectedCharts] = useState({
        summary: true,
        conversion: true,
        scores: true,
        performance: true,
        byDay: true,
        feedback: true
    });
    const reportRef = useRef();

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        fetchStats();
    }, [filterDate]);

    const fetchInitialData = async () => {
        try {
            const [datesRes, speakersRes] = await Promise.all([
                api.get('/surveys/admin/available-dates'),
                api.get('/speakers')
            ]);
            setAvailableDates(datesRes.data);
            setSpeakers(speakersRes.data);
        } catch (error) {
            console.error("Error fetching initial data", error);
        }
    };

    const fetchStats = async () => {
        try {
            setLoading(true);
            const statsRes = await api.get(`/surveys/admin/stats${filterDate ? `?date=${filterDate}` : ''}`);
            setStats(statsRes.data);
        } catch (error) {
            console.error("Error fetching stats", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        const originalElement = reportRef.current;
        
        // 1. Prepare elements for capture
        const sections = originalElement.querySelectorAll('[data-pdf-section]');
        const originalStyles = [];
        
        // Hide unselected sections and store original display
        sections.forEach(section => {
            const sectionId = section.getAttribute('data-pdf-section');
            originalStyles.push({ el: section, display: section.style.display });
            if (!selectedCharts[sectionId]) {
                section.style.display = 'none';
            }
        });

        // Add a temporary title element at the top
        const titleEl = document.createElement('div');
        titleEl.innerHTML = `
            <div style="text-align: center; margin-bottom: 40px; font-family: sans-serif; padding: 20px 0;">
                <h1 style="font-size: 32px; font-weight: bold; color: #1e293b; margin-bottom: 10px; text-transform: uppercase;">
                    NeuroUpdate 2026
                </h1>
                <h2 style="font-size: 20px; font-weight: bold; color: #334155; margin-bottom: 15px;">
                    Encuesta de Satisfacción
                </h2>
                <div style="display: inline-block; background-color: #f1f5f9; padding: 8px 20px; rounded-radius: 10px; border-radius: 12px; border: 1px solid #e2e8f0;">
                    <p style="font-size: 14px; color: #475569; font-weight: bold; margin: 0;">
                        ${filterDate ? `REPORTE DEL DÍA: ${new Date(filterDate + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}` : 'REPORTE GENERAL ACUMULADO'}
                    </p>
                </div>
            </div>
        `;
        originalElement.prepend(titleEl);

        try {
            // 2. Capture the entire container (preserving layout)
            const canvas = await html2canvas(originalElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#f8fafc',
                ignoreElements: (el) => el.classList.contains('pdf-exclude')
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imgProps = pdf.getImageProperties(imgData);
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            
            let heightLeft = pdfHeight;
            let position = 0;
            const pageHeight = pdf.internal.pageSize.getHeight();

            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - pdfHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`Reporte_NeuroUpdate_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error("PDF generation failed", error);
        } finally {
            // 3. Revert changes
            originalElement.removeChild(titleEl);
            originalStyles.forEach(style => {
                style.el.style.display = style.display;
            });
        }
    };

    const toggleChart = (id) => {
        setSelectedCharts(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
    );

    if (!stats) return <div className="p-8 text-center text-gray-500 text-xl font-bold">No hay datos suficientes para mostrar estadísticas.</div>;

    const completionData = {
        labels: ['Completado', 'Abandonado'],
        datasets: [{
            data: [stats.summary.total_completions, stats.summary.abandoned],
            backgroundColor: ['#10b981', '#f43f5e'],
            hoverOffset: 4
        }]
    };

    const ratedQuestions = stats.questions.filter(q => parseFloat(q.average_score) > 0 || q.type === 'nps');
    const questionLabels = ratedQuestions.map(q => {
        // Return an array to create multi-line labels
        const lines = [];
        const words = q.text.split(' ');
        let currentLine = '';
        
        words.forEach(word => {
            if ((currentLine + word).length > 35) {
                lines.push(currentLine.trim());
                currentLine = word + ' ';
            } else {
                currentLine += word + ' ';
            }
        });
        lines.push(currentLine.trim());
        
        // Add the stats as the last line
        lines.push(`[Promedio: ${q.average_score} | Votos: ${q.total_votes}]`);
        return lines;
    });
    const questionScores = ratedQuestions.map(q => q.average_score);

    const questionChartData = {
        labels: questionLabels,
        datasets: [{
            label: 'Puntuación Promedio',
            data: questionScores,
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgb(59, 130, 246)',
            borderWidth: 1
        }]
    };

    const responsesByDayData = {
        labels: (stats.responsesByDay || []).map(r => [r.date, `(${r.count} resp.)`]),
        datasets: [{
            label: 'Respuestas por Día',
            data: (stats.responsesByDay || []).map(r => r.count),
            backgroundColor: 'rgba(16, 185, 129, 0.6)',
            borderColor: 'rgb(16, 185, 129)',
            borderWidth: 1
        }]
    };

    const speakerLabels = stats.speakers.map(s => {
        const speaker = speakers.find(sp => sp.id === s.speaker_id);
        const name = speaker ? `${speaker.name} ${speaker.last_name}` : `ID: ${s.speaker_id}`;
        return [name, `[Promedio: ${s.average} | Votos: ${s.total_votes}]` ];
    });
    const speakerScores = stats.speakers.map(s => s.average);

    const speakerChartData = {
        labels: speakerLabels,
        datasets: [{
            label: 'Calificación Promedio',
            data: speakerScores,
            backgroundColor: 'rgba(139, 92, 246, 0.6)',
            borderColor: 'rgb(139, 92, 246)',
            borderWidth: 1
        }]
    };

    const CheckBox = ({ id, label }) => (
        <label className="pdf-exclude flex items-center gap-2 cursor-pointer bg-slate-100 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-all border border-slate-200 shadow-sm">
            <input 
                type="checkbox" 
                checked={selectedCharts[id]} 
                onChange={() => toggleChart(id)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-[10px] font-black uppercase text-slate-600 tracking-wider">Incluir en PDF</span>
        </label>
    );

    return (
        <div className="p-4 sm:p-8 bg-slate-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter">Estadísticas de Encuesta</h1>
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Selecciona los gráficos que deseas exportar a PDF</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                            <span className="text-[10px] font-black uppercase text-slate-400">Filtrar por día:</span>
                            <select 
                                className="bg-transparent border-0 text-sm font-bold text-slate-700 focus:ring-0 p-0 cursor-pointer"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                            >
                                <option value="">Todos los días</option>
                                {availableDates.map(date => (
                                    <option key={date} value={date}>
                                        {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                                    </option>
                                ))}
                            </select>
                            {filterDate && (
                                <button 
                                    onClick={() => setFilterDate('')}
                                    className="text-slate-400 hover:text-rose-500 transition-colors ml-2"
                                    title="Limpiar filtro"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        <button 
                            onClick={handleDownloadPDF}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-all flex items-center gap-2 group"
                        >
                            <span className="group-hover:scale-125 transition-transform">📥</span> Descargar Reporte Personalizado
                        </button>
                    </div>
                </div>

                <div ref={reportRef} className="space-y-8 bg-slate-50 p-2 rounded-3xl">
                    {/* Summary Cards */}
                    <div data-pdf-section="summary" className="relative group">
                        <div className="absolute -top-10 right-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                            <CheckBox id="summary" label="Resumen" />
                        </div>
                        <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 transition-all ${!selectedCharts.summary ? 'opacity-30' : ''}`}>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Total Ingresos</p>
                                <h2 className="text-3xl font-black text-slate-800">{stats.summary.total_entries}</h2>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                                <p className="text-xs font-black text-emerald-500 uppercase tracking-widest mb-1">Completados</p>
                                <h2 className="text-3xl font-black text-emerald-600">{stats.summary.total_completions}</h2>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                                <p className="text-xs font-black text-rose-500 uppercase tracking-widest mb-1">Abandonados</p>
                                <h2 className="text-3xl font-black text-rose-600">{stats.summary.abandoned}</h2>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                                <p className="text-xs font-black text-blue-500 uppercase tracking-widest mb-1">Tasa de Finalización</p>
                                <h2 className="text-3xl font-black text-blue-600">{stats.summary.completion_rate}%</h2>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Completion Donut */}
                        <div data-pdf-section="conversion" className="relative group lg:col-span-1">
                            <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                <CheckBox id="conversion" />
                            </div>
                            <div className={`bg-white p-8 rounded-3xl shadow-sm border border-slate-200 flex flex-col items-center h-full transition-all ${!selectedCharts.conversion ? 'opacity-30' : ''}`}>
                                <h3 className="text-sm font-black text-slate-800 uppercase mb-8 tracking-widest text-center">Conversión de Usuarios</h3>
                                <div className="w-full max-w-[250px]">
                                    <Doughnut data={completionData} options={{ cutout: '70%', plugins: { legend: { position: 'bottom' } } }} />
                                </div>
                            </div>
                        </div>

                        {/* General Scores Bar */}
                        <div data-pdf-section="scores" className="relative group lg:col-span-2">
                            <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                <CheckBox id="scores" />
                            </div>
                            <div className={`bg-white p-8 rounded-3xl shadow-sm border border-slate-200 transition-all ${!selectedCharts.scores ? 'opacity-30' : ''}`}>
                                <h3 className="text-sm font-black text-slate-800 uppercase mb-8 tracking-widest">Puntuación Promedio por Pregunta</h3>
                                <div style={{ height: '400px', position: 'relative' }}>
                                    <Bar data={questionChartData} options={{ 
                                        indexAxis: 'y',
                                        maintainAspectRatio: false,
                                        responsive: true,
                                        plugins: { 
                                            legend: { display: false },
                                            tooltip: {
                                                callbacks: {
                                                    afterLabel: (context) => {
                                                        const votes = ratedQuestions[context.dataIndex].total_votes;
                                                        const avg = ratedQuestions[context.dataIndex].average_score;
                                                        return `Total Votos: ${votes}\nPromedio: ${avg}`;
                                                    }
                                                }
                                            }
                                        },
                                        scales: { 
                                            x: { min: 0, max: 10 },
                                            y: {
                                                ticks: {
                                                    font: { size: 9 },
                                                    autoSkip: false
                                                }
                                            }
                                        }
                                    }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Responses by Day Chart */}
                    <div data-pdf-section="byDay" className="relative group">
                        <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                            <CheckBox id="byDay" />
                        </div>
                        <div className={`bg-white p-8 rounded-3xl shadow-sm border border-slate-200 transition-all ${!selectedCharts.byDay ? 'opacity-30' : ''}`}>
                            <h3 className="text-sm font-black text-slate-800 uppercase mb-8 tracking-widest">Actividad de Encuesta por Día</h3>
                            <div className="h-[300px]">
                                <Bar data={responsesByDayData} options={{ 
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
                                }} />
                            </div>
                        </div>
                    </div>

                    {/* Speaker Performance */}
                    <div data-pdf-section="performance" className="relative group">
                        <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                            <CheckBox id="performance" />
                        </div>
                        <div className={`bg-white p-8 rounded-3xl shadow-sm border border-slate-200 transition-all ${!selectedCharts.performance ? 'opacity-30' : ''}`}>
                            <h3 className="text-sm font-black text-slate-800 uppercase mb-8 tracking-widest">Desempeño de Conferencistas (Promedio)</h3>
                            <div className="h-[400px]">
                                <Bar data={speakerChartData} options={{ 
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: { 
                                        y: { min: 0, max: 10 },
                                        x: {
                                            ticks: {
                                                font: { size: 10 },
                                                autoSkip: false
                                            }
                                        }
                                    }
                                }} />
                            </div>
                        </div>
                    </div>

                    {/* Feedback Table */}
                    <div data-pdf-section="feedback" className="relative group">
                        <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                            <CheckBox id="feedback" />
                        </div>
                        <div className={`bg-white p-8 rounded-3xl shadow-sm border border-slate-200 transition-all ${!selectedCharts.feedback ? 'opacity-30' : ''}`}>
                            <h3 className="text-sm font-black text-slate-800 uppercase mb-8 tracking-widest">Comentarios y Feedback Abierto</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="py-4 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Pregunta</th>
                                            <th className="py-4 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Feedback / Respuestas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.questions.filter(q => q.answers && q.answers.length > 0).map((q, i) => (
                                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                                <td className="py-4 px-4 align-top">
                                                    <p className="text-xs font-bold text-slate-700">{q.text}</p>
                                                    <p className="text-[10px] text-slate-400 mt-1 uppercase font-black tracking-widest">{q.type}</p>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                                        {q.answers.map((ans, idx) => (
                                                            <span key={idx} className="inline-block bg-slate-100 px-3 py-1.5 rounded-lg text-[11px] text-slate-600 border border-slate-200">
                                                                {ans}
                                                            </span>
                                                        )).slice(0, 20)}
                                                        {q.answers.length > 20 && <span className="text-[10px] text-slate-400 self-center">+{q.answers.length - 20} más</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurveyStats;
